"use client";

import { useState } from "react";
import type { Category } from "../lib/types";
import { X, ChevronDown, ChevronUp, Search } from "lucide-react";

// Define category colors - same as in product-card.tsx
const categoryColors: Record<string, { bg: string; text: string }> = {
  "Mobility Solutions": { bg: "bg-blue-100", text: "text-blue-800" },
  "Smart Infrastructure": { bg: "bg-green-100", text: "text-green-800" },
  "Digital Twins": { bg: "bg-purple-100", text: "text-purple-800" },
  "Data Analytics": { bg: "bg-amber-100", text: "text-amber-800" },
  "User Access": { bg: "bg-red-100", text: "text-red-800" },
  "Booking Systems": { bg: "bg-cyan-100", text: "text-cyan-800" },
  "Payment Solutions": { bg: "bg-emerald-100", text: "text-emerald-800" },
  "Integration Tools": { bg: "bg-indigo-100", text: "text-indigo-800" },
};

interface Manufacturer {
  name: string;
  logo: string;
}

interface HorizontalFilterProps {
  categories: Category[];
  manufacturers: Manufacturer[];
  selectedCategories: Category[];
  selectedManufacturers: string[];
  onCategoryChange: (category: Category) => void;
  onManufacturerChange: (manufacturer: string) => void;
  onClearFilters: () => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export default function HorizontalFilter({
  categories,
  manufacturers,
  selectedCategories,
  selectedManufacturers,
  onCategoryChange,
  onManufacturerChange,
  onClearFilters,
  searchQuery,
  onSearchChange,
}: HorizontalFilterProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(true);
  const [manufacturerOpen, setManufacturerOpen] = useState(true);

  const hasActiveFilters =
    selectedCategories.length > 0 ||
    selectedManufacturers.length > 0 ||
    searchQuery.length > 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-8 transition-all duration-300 shadow-sm hover:shadow-md">
      {/* Filter Header - Always visible */}
      <div className="p-4 flex flex-wrap items-center justify-between gap-4">
        {/* <button
          type="button"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className={`inline-flex items-center px-4 py-2 border ${
            filtersOpen
              ? "border-purple-300 bg-purple-50"
              : "border-gray-300 bg-white"
          } rounded-md shadow-sm text-sm font-medium ${
            filtersOpen ? "text-purple-700" : "text-gray-700"
          } hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors`}
        >
          <Filter
            className={`mr-2 h-4 w-4 ${filtersOpen ? "text-purple-500" : ""}`}
          />
          Filters
          {filtersOpen ? (
            <ChevronUp className="ml-2 h-4 w-4" />
          ) : (
            <ChevronDown className="ml-2 h-4 w-4" />
          )}
        </button> */}

        {/* Search Bar - Integrated into the filter bar */}
        <div className="relative ml-4 flex-grow w-3/4">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 sm:text-sm shadow-sm hover:shadow transition-all duration-200"
            placeholder="Search products, categories, or partners..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange("")}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Active filters */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2">
            {selectedCategories.map((category) => {
              const categoryColor = categoryColors[category] || {
                bg: "bg-gray-100",
                text: "text-gray-800",
              };
              return (
                <button
                  key={category}
                  type="button"
                  className={`inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium ${categoryColor.bg} ${categoryColor.text}`}
                  onClick={() => onCategoryChange(category)}
                >
                  {category}
                  <X className="ml-1 h-3 w-3" />
                </button>
              );
            })}
            {selectedManufacturers.map((manufacturer) => (
              <button
                key={manufacturer}
                type="button"
                className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                onClick={() => onManufacturerChange(manufacturer)}
              >
                {manufacturer}
                <X className="ml-1 h-3 w-3" />
              </button>
            ))}

            {searchQuery && (
              <button
                type="button"
                className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                onClick={() => onSearchChange("")}
              >
                Search: {searchQuery}
                <X className="ml-1 h-3 w-3" />
              </button>
            )}

            {hasActiveFilters && (
              <button
                type="button"
                className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                onClick={onClearFilters}
              >
                Clear all
                <X className="ml-1 h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Expandable Filter Section */}
      {filtersOpen && (
        <div className="p-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-300">
          {/* Category filter - Visual Labels */}
          <div>
            <div
              className="flex items-center justify-between cursor-pointer mb-3"
              onClick={() => setCategoryOpen(!categoryOpen)}
            >
              <h3 className="text-sm font-medium text-gray-900">Categories</h3>
              {categoryOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>

            {categoryOpen && (
              <div className="max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 animate-in fade-in duration-200">
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => {
                    const categoryColor = categoryColors[category] || {
                      bg: "bg-gray-100",
                      text: "text-gray-800",
                    };
                    const isSelected = selectedCategories.includes(category);
                    return (
                      <button
                        key={category}
                        onClick={() => onCategoryChange(category)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${
                          categoryColor.bg
                        } ${categoryColor.text} transition-all duration-200 ${
                          isSelected
                            ? "ring-2 ring-offset-2 ring-blue-500"
                            : "hover:ring-1 hover:ring-blue-300"
                        }`}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Manufacturer filter - With Logos */}
          <div>
            <div
              className="flex items-center justify-between cursor-pointer mb-3"
              onClick={() => setManufacturerOpen(!manufacturerOpen)}
            >
              <h3 className="text-sm font-medium text-gray-900">
                Manufacturers
              </h3>
              {manufacturerOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>

            {manufacturerOpen && (
              <div className="max-h-60 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 animate-in fade-in duration-200">
                <div className="space-y-2">
                  {manufacturers.map((manufacturer) => {
                    const isSelected = selectedManufacturers.includes(
                      manufacturer.name
                    );
                    return (
                      <button
                        key={manufacturer.name}
                        onClick={() => onManufacturerChange(manufacturer.name)}
                        className={`flex items-center w-full p-2 rounded-lg transition-all duration-200 ${
                          isSelected
                            ? "bg-blue-50 border border-blue-200"
                            : "hover:bg-gray-50 border border-transparent"
                        }`}
                      >
                        <div className="h-8 w-8 flex-shrink-0 bg-white rounded-md border border-gray-200 overflow-hidden mr-3">
                          <img
                            src={manufacturer.logo || "/placeholder.svg"}
                            alt={manufacturer.name}
                            width={32}
                            height={32}
                            className="h-full w-full object-contain"
                          />
                        </div>
                        <span className="text-sm font-medium text-gray-700">
                          {manufacturer.name}
                        </span>
                        {isSelected && (
                          <X className="ml-auto h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
